import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dailyPlansApi, outcomesApi, tasksApi, goalsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import { Plus, Calendar, CheckCircle2, AlertTriangle, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { getToday, getStatusColor, getDepartmentColor, formatCurrency } from '../lib/utils';

const DEPARTMENTS = ["Marketing", "Sales", "Operations", "Finance", "R&D", "HR/People", "Admin"];
const METRIC_TYPES = ["Currency", "Count", "Percent", "Yes-No"];
const STATUSES = ["Not Started", "In Progress", "Done"];

export default function DailyPlan() {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [plan, setPlan] = useState(null);
  const [outcomes, setOutcomes] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOutcomeDialog, setShowOutcomeDialog] = useState(false);
  const [showSpecificityDialog, setShowSpecificityDialog] = useState(false);
  const [specificityErrors, setSpecificityErrors] = useState(null);
  const [pendingOutcome, setPendingOutcome] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [plansRes, goalsRes] = await Promise.all([
        dailyPlansApi.getAll({ date: selectedDate, user_id: user.id }),
        goalsApi.getAll()
      ]);
      
      setGoals(goalsRes.data);
      
      if (plansRes.data.length > 0) {
        setPlan(plansRes.data[0]);
        setOutcomes(plansRes.data[0].outcomes || []);
      } else {
        setPlan(null);
        setOutcomes([]);
      }
    } catch (error) {
      toast.error('Failed to load daily plan');
    } finally {
      setLoading(false);
    }
  };

  const createPlan = async () => {
    try {
      const response = await dailyPlansApi.create({
        date: selectedDate,
        user_id: user.id
      });
      setPlan(response.data);
      toast.success('Daily plan created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create plan');
    }
  };

  const handleCreateOutcome = async (outcomeData) => {
    try {
      const response = await outcomesApi.create({
        ...outcomeData,
        daily_plan_id: plan.id
      });
      setOutcomes([...outcomes, response.data]);
      setShowOutcomeDialog(false);
      toast.success('Outcome added');
    } catch (error) {
      if (error.response?.data?.detail?.guidance) {
        setSpecificityErrors(error.response.data.detail);
        setPendingOutcome(outcomeData);
        setShowOutcomeDialog(false);
        setShowSpecificityDialog(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to add outcome');
      }
    }
  };

  const handleUpdateOutcome = async (outcomeId, updates) => {
    try {
      await outcomesApi.update(outcomeId, updates);
      setOutcomes(outcomes.map(o => o.id === outcomeId ? { ...o, ...updates } : o));
      toast.success('Outcome updated');
    } catch (error) {
      toast.error('Failed to update outcome');
    }
  };

  const handleDeleteOutcome = async (outcomeId) => {
    if (!window.confirm('Delete this outcome and its tasks?')) return;
    try {
      await outcomesApi.delete(outcomeId);
      setOutcomes(outcomes.filter(o => o.id !== outcomeId));
      toast.success('Outcome deleted');
    } catch (error) {
      toast.error('Failed to delete outcome');
    }
  };

  const completionPercent = outcomes.length > 0
    ? Math.round(outcomes.filter(o => o.status === 'Done').length / outcomes.length * 100)
    : 0;

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 skeleton rounded-lg" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="daily-plan-page">
      <div className="flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">Daily Plan</h1>
          <p className="page-subtitle">Plan and track your daily outcomes</p>
        </div>
        <div className="flex items-center gap-4">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-44"
            data-testid="date-picker"
          />
        </div>
      </div>

      {/* Progress Summary */}
      <Card className="border border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Today's Progress</h3>
              <p className="text-slate-500">{outcomes.length} of 4 outcomes planned</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{completionPercent}%</div>
              <Progress value={completionPercent} className="w-32 h-2 mt-2" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Plan or Show Outcomes */}
      {!plan ? (
        <Card className="border border-slate-200">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold mb-2">No plan for {selectedDate}</h3>
            <p className="text-slate-500 mb-4">Create a daily plan to start adding outcomes</p>
            <Button onClick={createPlan} data-testid="create-plan-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Daily Plan
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Add Outcome Button */}
          {outcomes.length < 4 && (
            <Button onClick={() => setShowOutcomeDialog(true)} data-testid="add-outcome-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Outcome ({outcomes.length}/4)
            </Button>
          )}

          {/* Outcomes List */}
          <div className="space-y-4">
            {outcomes.map((outcome) => (
              <OutcomeCard
                key={outcome.id}
                outcome={outcome}
                goals={goals}
                onUpdate={handleUpdateOutcome}
                onDelete={handleDeleteOutcome}
              />
            ))}
            {outcomes.length === 0 && (
              <Card className="border border-dashed border-slate-300">
                <CardContent className="py-8 text-center text-slate-500">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p>No outcomes yet. Add up to 4 outcomes for today.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}

      {/* Add Outcome Dialog */}
      <Dialog open={showOutcomeDialog} onOpenChange={setShowOutcomeDialog}>
        <OutcomeDialog
          goals={goals}
          dailyPlanId={plan?.id}
          date={selectedDate}
          onSave={handleCreateOutcome}
          onClose={() => setShowOutcomeDialog(false)}
        />
      </Dialog>

      {/* Specificity Guide Dialog */}
      <Dialog open={showSpecificityDialog} onOpenChange={setShowSpecificityDialog}>
        <SpecificityGuideDialog
          errors={specificityErrors}
          pendingOutcome={pendingOutcome}
          onSave={(refined) => {
            handleCreateOutcome(refined);
            setShowSpecificityDialog(false);
          }}
          onClose={() => setShowSpecificityDialog(false)}
        />
      </Dialog>
    </div>
  );
}

function OutcomeCard({ outcome, goals, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(true);
  const [showTaskInput, setShowTaskInput] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [tasks, setTasks] = useState(outcome.tasks || []);
  const [localActual, setLocalActual] = useState(outcome.actual_value || 0);

  const goal = goals.find(g => g.id === outcome.goal_id);

  const handleAddTask = async () => {
    if (!newTask.trim()) return;
    try {
      const response = await tasksApi.create({
        outcome_id: outcome.id,
        description: newTask,
        status: 'Not Started'
      });
      setTasks([...tasks, response.data]);
      setNewTask('');
      setShowTaskInput(false);
      toast.success('Task added');
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const handleTaskStatus = async (taskId, status) => {
    try {
      await tasksApi.update(taskId, { status });
      setTasks(tasks.map(t => t.id === taskId ? { ...t, status } : t));
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await tasksApi.delete(taskId);
      setTasks(tasks.filter(t => t.id !== taskId));
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleActualChange = (value) => {
    setLocalActual(value);
  };

  const handleActualBlur = () => {
    if (localActual !== outcome.actual_value) {
      onUpdate(outcome.id, { actual_value: localActual });
    }
  };

  const handleStatusChange = async (newStatus) => {
    // If marking as Done for numeric metrics, require actual value
    if (newStatus === 'Done' && ['Currency', 'Count', 'Percent'].includes(outcome.metric_type)) {
      if (!localActual || localActual <= 0) {
        toast.error('Please enter the actual value achieved before marking as Done');
        return;
      }
    }
    
    try {
      await onUpdate(outcome.id, { status: newStatus, actual_value: localActual });
    } catch (error) {
      // Error is handled in parent
    }
  };

  const taskCompletion = tasks.length > 0
    ? Math.round(tasks.filter(t => t.status === 'Done').length / tasks.length * 100)
    : 0;

  // Calculate progress safely
  const progressPercent = outcome.metric_type === 'Yes-No' 
    ? (outcome.status === 'Done' ? 100 : 0)
    : (outcome.target_value > 0 ? Math.min(Math.round((localActual / outcome.target_value) * 100), 100) : 0);

  return (
    <Card className="border border-slate-200" data-testid={`outcome-card-${outcome.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className={getDepartmentColor(outcome.department)}>
                {outcome.department}
              </Badge>
              <Badge variant="outline" className={getStatusColor(outcome.status)}>
                {outcome.status}
              </Badge>
              {goal && (
                <Badge variant="outline" className="bg-slate-50">
                  Goal: {goal.title}
                </Badge>
              )}
            </div>
            <CardTitle className="text-base font-semibold">{outcome.statement}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(outcome.id)}
              data-testid={`delete-outcome-${outcome.id}`}
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent className="pt-0">
          {/* Metrics */}
          <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="text-xs text-slate-500">Target</Label>
              {outcome.metric_type === 'Yes-No' ? (
                <p className="font-semibold text-slate-600">Yes/No</p>
              ) : outcome.target_value > 0 ? (
                <p className="font-semibold">{outcome.target_value} {outcome.metric_type === 'Currency' ? '₹' : outcome.metric_type === 'Percent' ? '%' : ''}</p>
              ) : (
                <p className="text-amber-600 font-semibold">Not set</p>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500">Actual {outcome.metric_type !== 'Yes-No' && <span className="text-red-500">*</span>}</Label>
              {outcome.metric_type === 'Yes-No' ? (
                <p className="font-semibold text-slate-600 mt-1">{outcome.status === 'Done' ? 'Yes' : 'No'}</p>
              ) : (
                <Input
                  type="number"
                  value={localActual}
                  onChange={(e) => handleActualChange(parseFloat(e.target.value) || 0)}
                  onBlur={handleActualBlur}
                  className="h-8 mt-1"
                  placeholder="Enter achieved value"
                  data-testid={`actual-value-${outcome.id}`}
                />
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500">Status</Label>
              <Select
                value={outcome.status}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger className="h-8 mt-1" data-testid={`outcome-status-${outcome.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Progress */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-slate-600">Progress</span>
              <span className="text-sm font-medium">
                {outcome.target_value > 0 || outcome.metric_type === 'Yes-No' ? `${progressPercent}%` : 'N/A'}
              </span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>

          {/* Tasks */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold">Tasks ({tasks.filter(t => t.status === 'Done').length}/{tasks.length})</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowTaskInput(true)}
                data-testid={`add-task-btn-${outcome.id}`}
              >
                <Plus className="w-4 h-4 mr-1" /> Add Task
              </Button>
            </div>

            {showTaskInput && (
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="Task description..."
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
                  data-testid={`task-input-${outcome.id}`}
                />
                <Button onClick={handleAddTask} size="sm">Add</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowTaskInput(false)}>Cancel</Button>
              </div>
            )}

            <div className="space-y-2">
              {tasks.map(task => (
                <div key={task.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                  <Checkbox
                    checked={task.status === 'Done'}
                    onCheckedChange={(checked) => handleTaskStatus(task.id, checked ? 'Done' : 'Not Started')}
                    data-testid={`task-checkbox-${task.id}`}
                  />
                  <span className={`flex-1 text-sm ${task.status === 'Done' ? 'line-through text-slate-400' : ''}`}>
                    {task.description}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleDeleteTask(task.id)}
                  >
                    <Trash2 className="w-3 h-3 text-slate-400" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function OutcomeDialog({ goals, dailyPlanId, date, onSave, onClose }) {
  const [form, setForm] = useState({
    statement: '',
    department: 'Sales',
    metric_type: 'Currency',
    target_value: 0,
    actual_value: 0,
    deadline: date,
    goal_id: '',
    status: 'Not Started'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Add Outcome</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Outcome Statement *</Label>
          <Textarea
            value={form.statement}
            onChange={(e) => setForm({...form, statement: e.target.value})}
            placeholder="e.g., Book 3 sales calls with qualified leads"
            required
            data-testid="outcome-statement-input"
          />
          <p className="text-xs text-slate-500">Be specific: What exactly will be completed?</p>
        </div>

        <div className="space-y-2">
          <Label>Link to Goal *</Label>
          <Select value={form.goal_id} onValueChange={(v) => setForm({...form, goal_id: v})}>
            <SelectTrigger data-testid="outcome-goal-select">
              <SelectValue placeholder="Select a goal" />
            </SelectTrigger>
            <SelectContent>
              {goals.map(g => (
                <SelectItem key={g.id} value={g.id}>
                  {g.title} ({g.horizon})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Department</Label>
            <Select value={form.department} onValueChange={(v) => setForm({...form, department: v})}>
              <SelectTrigger data-testid="outcome-department-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Metric Type</Label>
            <Select value={form.metric_type} onValueChange={(v) => setForm({...form, metric_type: v})}>
              <SelectTrigger data-testid="outcome-metric-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METRIC_TYPES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Target Value *</Label>
            <Input
              type="number"
              value={form.target_value}
              onChange={(e) => setForm({...form, target_value: parseFloat(e.target.value) || 0})}
              placeholder="e.g., 3 or 50000"
              required
              data-testid="outcome-target-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Deadline *</Label>
            <Input
              type="date"
              value={form.deadline}
              onChange={(e) => setForm({...form, deadline: e.target.value})}
              required
              data-testid="outcome-deadline-input"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" data-testid="save-outcome-btn">Save Outcome</Button>
        </div>
      </form>
    </DialogContent>
  );
}

function SpecificityGuideDialog({ errors, pendingOutcome, onSave, onClose }) {
  const [form, setForm] = useState({
    what: '',
    when: pendingOutcome?.deadline || '',
    measure: ''
  });

  const generateStatement = () => {
    return `${form.what} by ${form.when} (Target: ${form.measure})`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const refinedOutcome = {
      ...pendingOutcome,
      statement: generateStatement(),
      deadline: form.when,
      target_value: parseFloat(form.measure) || pendingOutcome.target_value
    };
    onSave(refinedOutcome);
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-amber-600">
          <AlertTriangle className="w-5 h-5" />
          Make Your Outcome Specific
        </DialogTitle>
      </DialogHeader>
      
      <Alert className="bg-amber-50 border-amber-200">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-800">Your outcome needs more specificity</AlertTitle>
        <AlertDescription className="text-amber-700">
          {errors?.errors}
        </AlertDescription>
      </Alert>

      <form onSubmit={handleSubmit} className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label>1. What exactly will be completed?</Label>
          <Textarea
            value={form.what}
            onChange={(e) => setForm({...form, what: e.target.value})}
            placeholder="e.g., Book 3 sales calls with qualified leads from the CRM"
            required
            data-testid="specificity-what-input"
          />
        </div>

        <div className="space-y-2">
          <Label>2. By what date/time?</Label>
          <Input
            type="date"
            value={form.when}
            onChange={(e) => setForm({...form, when: e.target.value})}
            required
            data-testid="specificity-when-input"
          />
        </div>

        <div className="space-y-2">
          <Label>3. What number/₹/% defines success?</Label>
          <Input
            type="text"
            value={form.measure}
            onChange={(e) => setForm({...form, measure: e.target.value})}
            placeholder="e.g., 3, ₹50000, 80%"
            required
            data-testid="specificity-measure-input"
          />
        </div>

        {form.what && form.when && form.measure && (
          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <Label className="text-emerald-700">Refined Outcome:</Label>
            <p className="mt-1 font-medium text-emerald-900">{generateStatement()}</p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" data-testid="save-refined-outcome-btn">Save Refined Outcome</Button>
        </div>
      </form>
    </DialogContent>
  );
}
